import { filter, isArray } from 'lodash'
import {
  DQ_COLUMNS,
  EXTERNAL_COLUMNS,
  INTERNAL_COLUMNS,
  searchColumns,
} from '../views/search/searchConstants'

export const filterWithMultipleMatches = (collections, filterObj) => {
  if (isArray(collections)) {
    return filter(collections, filterObj)
  }
}

// Filter TCIN's only from the collections, Result --> ["52764237", "51945064", "52231985"]
export const filterTCINs = (collections) =>
  collections?.map((item) => item.tcin)

export const mapFiltersToArray = (filters) => {
  const appliedFilters = filters?.applied
  const resultArray = []
  if (appliedFilters) {
    for (const key in appliedFilters) {
      if (appliedFilters[key]) {
        const filterObject = {
          id: key,
          values: appliedFilters[key].values.map(({ value, displayValue }) => ({
            value,
            displayValue,
          })),
        }
        resultArray.push(filterObject)
      }
    }
  }

  return resultArray
}

/**
 * to convert an array of objects
 * @param {Array} array
 * @param {string} key
 * @returns {Object}
 */

export const arrayToObject = (array, key) =>
  array.reduce((acc, curr) => {
    acc[curr[key]] = curr
    return acc
  }, {})

/**
 * to convert our exisiting AttributeSchemaValue to canvas option format
 * @param {options} array // Options array with object of type AttributeSchemaValue
 */

export const formatOptions = (options) => {
  return options.map((option) => ({
    value: option.value,
    id: JSON.stringify(option.value),
    label: option.displayValue,
  }))
}

/**
 * to convert object to array
 * @param {Object} filterObject
 * @returns {Object}
 */

export const convertFiltersToArray = (filterObject) => {
  if (!filterObject || typeof filterObject !== 'object') {
    return []
  }

  return Object.keys(filterObject).map((key) => filterObject[key])
}

/**
 * Checks if the current domain includes 'itemhub'.
 */
export const isItemhubDomain = window.location.host.includes('itemhub')

// Detect the Partneronline | External or Internal users
export const isPartnerOnline = () => {
  const url = window.location.href
  return /(^([^/]+:)?\/{2,3}[^/]+?(\.partnersonline\.com)(:|\/|$))/i.test(url)
}
const isItemHubDomainExternal = () => {
  const regex =
    /^https:\/\/itemhub\.(?:[a-zA-Z]+\.)?partnersonline\.com(?:\/.*)?$/i // Itemhub External environment - Partners online
  return regex.test(window.location.href)
}
export const isItemhubDomainInInternal = () => {
  // const regex = /^https:\/\/itemhub\.(\w+)\.target\.com\/(\w+)\/.*$/i // Itemhub Internal environment - dev|prod.target.com
  const regex = /^https:\/\/itemhub\.(?:[a-zA-Z]+\.)?target\.com(?:\/.*)?$/i
  // const regex = /^https:\/\/itemhub\.(\w+)\.target\.com\/(\w+)\/?$/i
  return regex.test(window.location.href)
}
export const LoginRedirectPath = () => {
  let url = `${window.location.origin}/auth/login`
  if (isItemHubDomainExternal() || isItemhubDomainInInternal()) {
    url = `${window.location.origin}/${window.location.pathname.split('/')[1]}`
  }
  return url
}

export const findAppPath = () => {
  const localhostLists = ['localhost', 'localhost.target.com']
  const hostname = window.location.hostname
  const isLocal = localhostLists.includes(hostname)

  if (isLocal) {
    return ''
  }

  const pathSegments = window.location.pathname.split('/')
  const dynamicPath = pathSegments[1] ? `/${pathSegments[1]}` : ''

  if (isItemhubDomainInInternal() || isItemHubDomainExternal()) {
    return dynamicPath
  }

  return ''
}

/**
 * Determines if the current domain is associated with Item Hub (internal or external).
 */
export const isItemHub =
  isItemhubDomainInInternal() || isItemHubDomainExternal()

export const getUserColumns = (userType, roles) => {
  let displayColumns = userType === 'I' ? INTERNAL_COLUMNS : EXTERNAL_COLUMNS
  if (userType === 'I' && roles.includes('VALIDATOR')) {
    displayColumns = DQ_COLUMNS
  }
  const displaySet = new Set(displayColumns)
  return searchColumns.filter((item) => displaySet.has(item.id))
}
export const typeMapper = {
  vendor: 'string',
  productBrand: 'number',
}
export const mapValueTypeWithId = (id, value) => {
  const type = typeMapper[id]

  switch (type) {
    case 'string':
      return String(value)
    case 'number':
      return Number(value)
    default:
      return value
  }
}

export function writeFiltersToLocalStorage(key, obj) {
  const value = readJsonFromLocalStorage(key)
  if (value?.uId !== btoa(obj?.uId)) {
    localStorage.removeItem(key)
  }
  const data = {
    filters: {
      ...obj?.filters,
      searchText: obj?.searchText,
    },
    savedSearch: {
      activeSavedSearchId: obj?.savedSearch?.activeSavedSearchId,
    },
    uId: btoa(obj?.uId),
  }
  localStorage.setItem(key, JSON.stringify(data))
}
export function readJsonFromLocalStorage(key) {
  const localValue = localStorage.getItem(key)
  return localValue ? JSON.parse(localValue) : ''
}

export function setMyCustomiseColumns(defaultColumns, myColumns) {
  // Create a mapping from colId to default column
  const columnMap = Object.fromEntries(
    defaultColumns.map((col) => [col.colId, col]),
  )
  // Initialize output with the selection column
  const output = [
    {
      ...columnMap['selection'],
      colId: 'selection',
      width: columnMap['selection']?.width,
    },
  ]

  // Generate the output based on myColumns order, excluding non-default columns
  myColumns.forEach((myCol) => {
    const defaultCol = columnMap[myCol.id]
    if (defaultCol) {
      output.push({
        ...defaultCol,
        colId: myCol.id,
        width: myCol.width,
        visible: myCol.visible, // Include the visibility from myColumns
      })
    }
  })

  // Add remaining default columns that are not in myColumns
  const remainingColumns = defaultColumns.filter(
    (col) =>
      !myColumns.some((myCol) => myCol.id === col.colId) &&
      col.colId !== 'selection',
  )
  output.push(...remainingColumns)
  return output
}

// Filter the Object by department with the List of department Id's
export const filteredDepartmentLists = (collections, filterObj) => {
  if (isArray(collections) && isArray(filterObj)) {
    return filterObj.filter((item) => collections.includes(item.department))
  }
}

export const filteredItemWithStatus = (statusCollection, selectedItems) => {
  if (isArray(statusCollection) && isArray(selectedItems)) {
    return selectedItems.filter((item) =>
      statusCollection.includes(item?.lifeCycleStatus),
    )
  }
}
export const isMaintainableItem = (isMaintainable, isuStatus) => {
  //isMaintainable checked with API.
  //isuStatus  History item should not be maintainable
  const NonMaintainableISUStatusList = ['History']
  return isMaintainable && !NonMaintainableISUStatusList.includes(isuStatus)
}
